import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import clsx from 'clsx';
import InfoTip from '@/components/InfoTip';

export interface DashboardKPITileProps {
  label: string;
  value: string;
  delta?: number;
  caption?: string;
  accent?: boolean;
  /** Optional hover/focus explanation shown as an info tooltip beside the label. */
  info?: ReactNode;
}

export default function DashboardKPITile({
  label,
  value,
  delta,
  caption,
  accent,
  info,
}: DashboardKPITileProps) {
  const positive = (delta ?? 0) >= 0;
  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={clsx('panel p-5', accent ? 'border-gold-bright/40' : '')}
    >
      <p className="label-small-caps flex items-center gap-1">
        {label}
        {info ? <InfoTip>{info}</InfoTip> : null}
      </p>
      <p
        className={clsx(
          'font-cinzel text-[56px] leading-none mt-2 tabular-nums',
          accent ? 'text-gold-bright' : 'text-cream',
        )}
      >
        {value}
      </p>
      {typeof delta === 'number' ? (
        <p
          className={clsx(
            'mt-3 flex items-center gap-1 text-xs font-mono',
            positive ? 'text-mint' : 'text-blood',
          )}
        >
          {positive ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
          {Math.abs(delta).toFixed(1)}%
        </p>
      ) : null}
      {caption ? <p className="font-inter text-xs text-cream-soft mt-2">{caption}</p> : null}
    </div>
  );
}
